/**
 * "Nova consulta": agendar no SISAC numa tela só, em três passos (#1011; fase
 * 2 da agenda da clínica, 24/10).
 *
 * 1. Especialidade e médico; "Ver vagas" confirma e vira uma linha com "Alterar".
 * 2. O dia e o horário no calendário; "Continuar" confirma a vaga.
 * 3. Os dados do agendamento, como no "Horário Vago" do SISAC: o paciente
 *    (vinculado, procurado ou sem cadastro), local, origem, tipo, nascimento,
 *    CEP, convênio, plano e o resto. "Agendar consulta" é o único primário.
 *
 * ── Só tela ──────────────────────────────────────────────────────────────
 *
 * O que a pessoa digita e escolhe mora aqui: os campos, a vaga, o formulário.
 * O que depende do SISAC chega pronto por props (o paciente, as vagas, as
 * listas, o envio), e cada pedido sai por um `ao…`.
 *
 * ── Um clique só ─────────────────────────────────────────────────────────
 *
 * Enquanto o envio não volta, tudo trava: um segundo clique daria "vaga
 * ocupada" com a primeira marcação feita.
 *
 * ── Duas formas ──────────────────────────────────────────────────────────
 *
 * `cartoes` (o card da oportunidade): as três etapas empilhadas, a feita vira
 * uma linha com "Alterar". `passos` (o painel do Atendimento, 25/10): uma
 * etapa por vez, com a trilha 1-2-3 no topo e "Voltar" embaixo, para a pessoa
 * não rolar a coluna atrás do próximo campo.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Botao, CalendarioDeVagas, CampoEscolha, Icone } from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  ESPERA_DO_SISAC,
  type EstadoDasVagas,
  type EstadoDoEnvio,
  type OpcaoDaAgenda,
} from '../lib/consultas-na-tela';
import {
  errosDoFormulario,
  formularioInicial,
  opcaoPeloNome,
  type FormularioDoAgendamento,
} from '../lib/formulario-do-agendamento';
import type { ListaDoFormulario, PacienteDaConsulta } from '../lib/consultas-na-tela';
import { DadosDoAgendamento } from './dados-do-agendamento';
import { ValorDaConsulta } from './valor-da-consulta';
import './nova-consulta.css';

export type EscolhaDoMedico = { readonly especialidadeId: string; readonly medicoId: string };

export function NovaConsulta({
  especialidades,
  medicos,
  inicial,
  paciente,
  buscaDoPaciente,
  vagas,
  listas,
  envio,
  origemPadrao = 'Atendimento',
  forma = 'cartoes',
  noComeco = null,
  telefoneDoContato = '',
  diasDeVagas = 14,
  hoje = new Date(),
  aoVerVagas,
  aoEscolherConvenio,
  aoRecarregarVagas,
  aoMarcar,
  aoCancelar,
}: {
  especialidades: readonly OpcaoDaAgenda[];
  medicos: readonly OpcaoDaAgenda[];
  /** O que a ficha já tem, para os campos não começarem vazios. */
  inicial?: Partial<EscolhaDoMedico>;
  paciente: PacienteDaConsulta;
  /** O bloco que procura e vincula o paciente no SISAC; nulo sem cliente. */
  buscaDoPaciente: ReactNode;
  /** As vagas do médico da última confirmação de "Ver vagas". */
  vagas: EstadoDasVagas;
  listas: {
    readonly convenios: ListaDoFormulario;
    readonly planos: ListaDoFormulario;
    readonly origens: ListaDoFormulario;
    readonly locais: ListaDoFormulario;
  };
  envio: EstadoDoEnvio;
  /** O nome da origem que vem marcada: "WhatsApp" quando se agenda da conversa. */
  origemPadrao?: string;
  /** Etapas empilhadas (o card) ou uma por vez (o painel do Atendimento). */
  forma?: 'cartoes' | 'passos';
  /** O que vem antes do médico na etapa 1: a escolha da oportunidade, no Atendimento. */
  noComeco?: ReactNode;
  /** O telefone do lead (DDD e número): o WhatsApp e o celular começam com ele. */
  telefoneDoContato?: string;
  /** Quantos dias à frente as vagas olham, para o texto de lista vazia. */
  diasDeVagas?: number;
  hoje?: Date;
  aoVerVagas: (escolha: EscolhaDoMedico) => void;
  aoEscolherConvenio: (codigo: string) => void;
  aoRecarregarVagas: () => void;
  aoMarcar: (vagaId: string, formulario: FormularioDoAgendamento) => void;
  aoCancelar: () => void;
}): ReactNode {
  const [especialidadeId, definirEspecialidadeId] = useState(inicial?.especialidadeId ?? '');
  const [medicoId, definirMedicoId] = useState(inicial?.medicoId ?? '');
  const [confirmada, definirConfirmada] = useState<EscolhaDoMedico | null>(null);
  const [alterando, definirAlterando] = useState(false);
  const [vagaId, definirVagaId] = useState<string | null>(null);
  const [vagaConfirmada, definirVagaConfirmada] = useState(false);
  const [tentou, definirTentou] = useState(false);
  const [formulario, definirFormulario] = useState<FormularioDoAgendamento>(() =>
    formularioInicial({
      dados: paciente.situacao === 'vinculado' ? paciente.dados : null,
      local: '',
      origem: '',
      telefoneDoContato,
    }),
  );
  const mudar = (mudanca: Partial<FormularioDoAgendamento>) =>
    definirFormulario((atual) => ({ ...atual, ...mudanca }));

  /*
   * Ajustes quando a prop muda, no próprio render (o padrão do React para isso,
   * sem efeito): a vaga tomada no envio deixa de estar escolhida; o paciente
   * vinculado agora preenche o que ainda está vazio; e as listas, ao chegar,
   * marcam o local e a origem de costume.
   */
  const ocupada = vagas.situacao === 'pronta' && vagas.ocupada;
  const [ocupadaVista, definirOcupadaVista] = useState(ocupada);
  if (ocupada !== ocupadaVista) {
    definirOcupadaVista(ocupada);
    if (ocupada) {
      definirVagaId(null);
      definirVagaConfirmada(false);
    }
  }
  const codigoDoVinculado = paciente.situacao === 'vinculado' ? paciente.paciente.codigo : null;
  const [vinculadoVisto, definirVinculadoVisto] = useState(codigoDoVinculado);
  if (codigoDoVinculado !== vinculadoVisto) {
    definirVinculadoVisto(codigoDoVinculado);
    if (paciente.situacao === 'vinculado' && paciente.dados) {
      const doCadastro = formularioInicial({
        dados: paciente.dados,
        local: '',
        origem: '',
        telefoneDoContato,
      });
      definirFormulario((atual) => ({
        ...atual,
        semCadastro: false,
        nascimento: atual.nascimento || doCadastro.nascimento,
        sexo: atual.sexo || doCadastro.sexo,
        cep: atual.cep || doCadastro.cep,
        convenio: atual.convenio || doCadastro.convenio,
        plano: atual.plano || doCadastro.plano,
        matricula: atual.matricula || doCadastro.matricula,
        // O celular de partida é o telefone do lead: o do cadastro, quando chega, vale mais.
        celular:
          atual.celular && atual.celular !== telefoneDoContato ? atual.celular : doCadastro.celular,
        whatsapp: atual.whatsapp || doCadastro.whatsapp,
        email: atual.email || doCadastro.email,
      }));
    }
  }
  if (formulario.local === '' && listas.locais.situacao === 'pronta') {
    // O primeiro local da clínica: na Santa Rafaela, a própria clínica.
    const local = listas.locais.opcoes[0]?.valor ?? '';
    if (local) mudar({ local });
  }
  if (formulario.origem === '' && listas.origens.situacao === 'pronta') {
    const origem = opcaoPeloNome(listas.origens.opcoes, origemPadrao);
    if (origem) mudar({ origem });
  }

  // Os planos dependem do convênio: o modelo os lê quando ele muda, inclusive no pré-preenchimento.
  useEffect(() => {
    aoEscolherConvenio(formulario.convenio);
  }, [formulario.convenio, aoEscolherConvenio]);

  const enviando = envio.situacao === 'enviando';
  const nomeDe = (lista: readonly OpcaoDaAgenda[], id: string) =>
    lista.find((opcao) => opcao.id === id)?.nome ?? '';
  const medicoDaVaga = confirmada ? nomeDe(medicos, confirmada.medicoId) : '';
  const especialidadeDaVaga = confirmada ? nomeDe(especialidades, confirmada.especialidadeId) : '';
  const vaga =
    vagas.situacao === 'pronta' ? (vagas.lista.find((v) => v.id === vagaId) ?? null) : null;
  const etapa2Aberta = confirmada !== null && !alterando;
  const etapa3Aberta = etapa2Aberta && vaga !== null && vagaConfirmada;

  const temPaciente = paciente.situacao === 'vinculado';
  const erros = errosDoFormulario(formulario, { temPaciente, hoje });
  const pronto = Object.keys(erros).length === 0;
  const nomeDoPaciente =
    paciente.situacao === 'vinculado'
      ? paciente.paciente.nome
      : formulario.semCadastro
        ? formulario.nome.trim()
        : '';

  const verVagas = () => {
    if (confirmada?.medicoId !== medicoId) {
      definirVagaId(null);
      definirVagaConfirmada(false);
    }
    const escolha = { especialidadeId, medicoId };
    definirConfirmada(escolha);
    definirAlterando(false);
    aoVerVagas(escolha);
  };
  const escolherVaga = (id: string) => {
    if (!enviando) definirVagaId(id);
  };
  const agendar = () => {
    definirTentou(true);
    if (vaga && pronto && !enviando) aoMarcar(vaga.id, formulario);
  };

  const titulo = (numero: 1 | 2 | 3, nome: string, situacao: 'atual' | 'feita' | 'espera') => (
    <div className="nova-consulta__cab-etapa">
      <span className="nova-consulta__numero" aria-hidden="true">
        {situacao === 'feita' ? <Icone nome="check" /> : numero}
      </span>
      <h4 className="nova-consulta__titulo-etapa" id={`nova-consulta-etapa-${numero}`}>
        {nome}
      </h4>
    </div>
  );

  const cancelar = (rotulo: string, aoClicar: () => void) => (
    <Botao
      variante="fantasma"
      miudo={forma === 'cartoes'}
      desabilitado={enviando}
      motivo={ESPERA_DO_SISAC}
      onClick={aoClicar}
    >
      {rotulo}
    </Botao>
  );

  const etapa1 = (
    <>
      {noComeco}
      {medicos.length === 0 || especialidades.length === 0 ? (
        <p className="nova-consulta__nota" role="status">
          A lista de médicos e especialidades vem do SISAC e ainda está vazia. Ela aparece aqui
          assim que a integração for ligada.
        </p>
      ) : (
        <p className="nova-consulta__apoio">
          Escolha a especialidade e o médico. A lista vem do SISAC.
        </p>
      )}
      <form
        className="nova-consulta__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (medicoId && especialidadeId) verVagas();
        }}
      >
        <div className="nova-consulta__dupla">
          <CampoEscolha
            id="nova-consulta-especialidade"
            rotulo="Especialidade"
            buscavel
            vazio="Escolha"
            valor={especialidadeId}
            opcoes={especialidades.map((opcao) => ({ valor: opcao.id, rotulo: opcao.nome }))}
            aoEscolher={definirEspecialidadeId}
          />
          <CampoEscolha
            id="nova-consulta-medico"
            rotulo="Médico"
            buscavel
            vazio="Escolha"
            valor={medicoId}
            opcoes={medicos.map((opcao) => ({ valor: opcao.id, rotulo: opcao.nome }))}
            aoEscolher={definirMedicoId}
          />
        </div>
        {medicoId && especialidadeId && <ValorDaConsulta medicoId={medicoId} />}
        <div className="nova-consulta__acoes">
          {forma === 'passos' && cancelar('Cancelar', aoCancelar)}
          <Botao
            variante="primario"
            type="submit"
            desabilitado={!medicoId || !especialidadeId}
            motivo="Escolha a especialidade e o médico."
          >
            Ver vagas
          </Botao>
        </div>
      </form>
    </>
  );

  const etapa2 = (
    <>
      {ocupada && (
        <Faixa tom="bloqueado" acao={{ rotulo: 'Recarregar vagas', aoClicar: aoRecarregarVagas }}>
          Esta vaga já foi ocupada. Recarregue as vagas.
        </Faixa>
      )}
      {vagas.situacao === 'carregando' ? (
        <Carregando forma="lista" linhas={3} rotulo="Buscando as vagas no SISAC" />
      ) : vagas.situacao === 'erro' ? (
        <div className="nova-consulta__vazio">
          <p className="nova-consulta__erro" role="alert">
            {vagas.recado}
          </p>
          <Botao variante="secundario" onClick={aoRecarregarVagas}>
            Tentar de novo
          </Botao>
        </div>
      ) : vagas.lista.length === 0 ? (
        <div className="nova-consulta__vazio">
          <Icone nome="calendario" />
          <p className="nova-consulta__nota">
            Nenhuma vaga livre para {medicoDaVaga} nos próximos {diasDeVagas} dias.
          </p>
          <Botao variante="secundario" onClick={() => definirAlterando(true)}>
            Trocar médico
          </Botao>
        </div>
      ) : (
        <>
          <CalendarioDeVagas
            rotulo={`Vagas livres de ${medicoDaVaga}`}
            vagas={vagas.lista}
            escolhida={vagaId}
            aoEscolher={escolherVaga}
            motivoTravado={enviando ? ESPERA_DO_SISAC : undefined}
          />
          {vagas.cortada && (
            <p className="nova-consulta__apoio">
              A lista mostra as primeiras vagas livres. Há outras mais adiante.
            </p>
          )}
          {!ocupada && (
            <div>
              <Botao
                variante="fantasma"
                miudo
                desabilitado={enviando}
                motivo={ESPERA_DO_SISAC}
                onClick={aoRecarregarVagas}
              >
                <Icone nome="repetir" />
                Recarregar vagas
              </Botao>
            </div>
          )}
        </>
      )}
      <div className="nova-consulta__acoes">
        {forma === 'passos' && cancelar('Voltar', () => definirAlterando(true))}
        <Botao
          variante="primario"
          desabilitado={vaga === null}
          motivo="Escolha o dia e o horário."
          onClick={() => definirVagaConfirmada(true)}
        >
          Continuar
        </Botao>
      </div>
    </>
  );

  const etapa3 = (
    <>
      <DadosDoAgendamento
        formulario={formulario}
        erros={tentou ? erros : {}}
        listas={listas}
        paciente={paciente}
        buscaDoPaciente={buscaDoPaciente}
        especialidade={especialidadeDaVaga}
        hoje={hoje}
        travado={enviando}
        aoMudar={mudar}
      />

      <div className="nova-consulta__final">
        {envio.situacao === 'fora-do-ar' && (
          <Faixa tom="bloqueado" acao={{ rotulo: 'Tentar de novo', aoClicar: agendar }}>
            O SISAC não respondeu. Nada foi agendado. Tente de novo em instantes.
          </Faixa>
        )}
        {envio.situacao === 'conflito' && (
          <p className="nova-consulta__erro" role="alert">
            Outra pessoa mudou esta consulta enquanto você agendava. Nada foi agendado. Cancele e
            abra a consulta de novo.
          </p>
        )}
        {envio.situacao === 'erro' && (
          <p className="nova-consulta__erro" role="alert">
            {envio.recado}
          </p>
        )}
        {tentou && !pronto && (
          <p className="nova-consulta__erro" role="alert">
            Preencha os campos marcados para agendar.
          </p>
        )}
        <div className="nova-consulta__linha-final">
          <p id="nova-consulta-resumo" className="nova-consulta__resumo">
            {[nomeDoPaciente || 'Paciente a escolher', medicoDaVaga, vaga?.quandoPorExtenso]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {forma === 'passos' && cancelar('Voltar', () => definirVagaConfirmada(false))}
          <Botao
            variante="primario"
            desabilitado={enviando}
            motivo={ESPERA_DO_SISAC}
            aria-describedby="nova-consulta-resumo"
            aria-busy={enviando || undefined}
            onClick={agendar}
          >
            {enviando ? 'Agendando…' : 'Agendar consulta'}
          </Botao>
        </div>
      </div>
    </>
  );

  if (forma === 'passos') {
    const passo = etapa3Aberta ? 3 : etapa2Aberta ? 2 : 1;
    const passos = [
      { numero: 1, nome: 'Médico', ir: () => definirAlterando(true) },
      { numero: 2, nome: 'Dia e horário', ir: () => definirVagaConfirmada(false) },
      { numero: 3, nome: 'Dados', ir: () => undefined },
    ] as const;
    return (
      <div className="nova-consulta nova-consulta--passos">
        <ol className="nova-consulta__trilha" aria-label="Etapas do agendamento">
          {passos.map(({ numero, nome, ir }) => {
            const situacao = numero < passo ? 'feita' : numero === passo ? 'atual' : 'espera';
            return (
              <li
                key={numero}
                className={`nova-consulta__marco nova-consulta__marco--${situacao}`}
                aria-current={situacao === 'atual' ? 'step' : undefined}
              >
                {situacao === 'feita' ? (
                  <button
                    type="button"
                    className="nova-consulta__marco-botao"
                    disabled={enviando}
                    title={enviando ? ESPERA_DO_SISAC : `Voltar para ${nome.toLowerCase()}`}
                    onClick={ir}
                  >
                    <span className="nova-consulta__numero" aria-hidden="true">
                      <Icone nome="check" />
                    </span>
                    {nome}
                  </button>
                ) : (
                  <span className="nova-consulta__marco-botao">
                    <span className="nova-consulta__numero" aria-hidden="true">
                      {numero}
                    </span>
                    {nome}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
        {passo > 1 && (
          <p className="nova-consulta__escolhas">
            {[
              `${medicoDaVaga} · ${especialidadeDaVaga}`,
              passo === 3 ? vaga?.quandoPorExtenso : null,
            ]
              .filter(Boolean)
              .join(' — ')}
          </p>
        )}
        {/* A chave reinicia a entrada a cada troca de etapa: a nova chega deslizando. */}
        <section
          key={passo}
          className="nova-consulta__passo"
          aria-labelledby={`nova-consulta-etapa-${passo}`}
        >
          {titulo(
            passo,
            ['Médico e especialidade', 'Dia e horário', 'Dados do agendamento'][passo - 1] ?? '',
            'atual',
          )}
          {passo === 1 ? etapa1 : passo === 2 ? etapa2 : etapa3}
        </section>
      </div>
    );
  }

  return (
    <div className="nova-consulta">
      <div className="nova-consulta__topo">
        <h3 className="nova-consulta__titulo">Nova consulta</h3>
        {cancelar('Cancelar', aoCancelar)}
      </div>

      {etapa2Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--feita"
          aria-labelledby="nova-consulta-etapa-1"
        >
          <div className="nova-consulta__cab-etapa">
            <span className="nova-consulta__numero" aria-hidden="true">
              <Icone nome="check" />
            </span>
            <h4 className="nova-consulta__titulo-etapa" id="nova-consulta-etapa-1">
              Médico e especialidade
            </h4>
            <span className="nova-consulta__valor">
              {medicoDaVaga} · {especialidadeDaVaga}
              <Botao
                variante="fantasma"
                miudo
                desabilitado={enviando}
                motivo={ESPERA_DO_SISAC}
                onClick={() => definirAlterando(true)}
              >
                Alterar
              </Botao>
            </span>
          </div>
        </section>
      ) : (
        <section className="nova-consulta__etapa" aria-labelledby="nova-consulta-etapa-1">
          {titulo(1, 'Médico e especialidade', 'atual')}
          {etapa1}
        </section>
      )}

      {!etapa2Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--espera"
          aria-labelledby="nova-consulta-etapa-2"
        >
          {titulo(2, 'Dia e horário', 'espera')}
          <p className="nova-consulta__apoio">Escolha o médico para ver os dias livres dele.</p>
        </section>
      ) : etapa3Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--feita"
          aria-labelledby="nova-consulta-etapa-2"
        >
          <div className="nova-consulta__cab-etapa">
            <span className="nova-consulta__numero" aria-hidden="true">
              <Icone nome="check" />
            </span>
            <h4 className="nova-consulta__titulo-etapa" id="nova-consulta-etapa-2">
              Dia e horário
            </h4>
            <span className="nova-consulta__valor">
              {vaga?.quandoPorExtenso}
              <Botao
                variante="fantasma"
                miudo
                desabilitado={enviando}
                motivo={ESPERA_DO_SISAC}
                onClick={() => definirVagaConfirmada(false)}
              >
                Alterar
              </Botao>
            </span>
          </div>
        </section>
      ) : (
        <section className="nova-consulta__etapa" aria-labelledby="nova-consulta-etapa-2">
          {titulo(2, 'Dia e horário', 'atual')}
          {etapa2}
        </section>
      )}

      {!etapa3Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--espera"
          aria-labelledby="nova-consulta-etapa-3"
        >
          {titulo(3, 'Dados do agendamento', 'espera')}
          <p className="nova-consulta__apoio">
            Depois da vaga, o paciente e os dados que o SISAC pede.
          </p>
        </section>
      ) : (
        <section className="nova-consulta__etapa" aria-labelledby="nova-consulta-etapa-3">
          {titulo(3, 'Dados do agendamento', 'atual')}
          {etapa3}
        </section>
      )}
    </div>
  );
}
